// src/utils/analytics.ts
// Client-side analytics manager — combines the browser fingerprint with live
// session metrics and reports them to the server via 'update-user-analytics'.
// WebRTC connection stats are reported separately by MediaSoupClient
// ('update-connection-stats'), so this manager does not touch transports.
import { getFingerprint } from './fingerprint';
import { getSessionTracker, destroySessionTracker } from './sessionMetrics';
const UPDATE_INTERVAL_MS = 30000; // Report every 30 seconds
class AnalyticsManager {
    constructor() {
        this.socket = null;
        this.fingerprint = null;
        this.updateInterval = null;
        this.initialized = false;
    }
    /**
     * Initialize with a connected Socket.IO socket. Collects the fingerprint,
     * starts the session tracker, sends an immediate report, then reports on
     * an interval.
     */
    async initialize(socket) {
        if (this.initialized && this.socket === socket) {
            return;
        }
        this.socket = socket;
        try {
            this.fingerprint = await getFingerprint();
            console.log('🔍 Fingerprint ready:', this.fingerprint.fingerprintId);
        }
        catch (error) {
            console.error('❌ Fingerprint generation failed:', error);
        }
        // Ensure the session tracker is running
        getSessionTracker();
        // Immediate first report
        await this.sendUpdate();
        // Periodic reports
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        this.updateInterval = setInterval(() => {
            this.sendUpdate();
        }, UPDATE_INTERVAL_MS);
        this.initialized = true;
        console.log('📊 Analytics manager initialized');
    }
    /**
     * Track webcam on/off state (feeds webcamOnTimeMs) and push an update
     * so the host's analytics panel reflects the change promptly.
     */
    setWebcamStatus(isOn) {
        getSessionTracker().setWebcamStatus(isOn);
        this.sendUpdate();
    }
    /**
     * Increment the session message counter.
     */
    incrementMessageCount() {
        getSessionTracker().incrementMessageCount();
    }
    async sendUpdate() {
        if (!this.socket || !this.socket.connected) {
            return;
        }
        try {
            const sessionMetrics = await getSessionTracker().getMetrics();
            this.socket.emit('update-user-analytics', {
                fingerprint: this.fingerprint,
                sessionMetrics
            });
        }
        catch (error) {
            console.error('❌ Failed to send analytics update:', error);
        }
    }
    destroy() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
        destroySessionTracker();
        this.socket = null;
        this.fingerprint = null;
        this.initialized = false;
    }
}
// Singleton (matches getSessionTracker / destroySessionTracker pattern)
let instance = null;
export function getAnalyticsManager() {
    if (!instance) {
        instance = new AnalyticsManager();
    }
    return instance;
}
export function destroyAnalyticsManager() {
    if (instance) {
        instance.destroy();
        instance = null;
    }
}
